import Image from 'next/image';
import clsx from 'clsx';
import { Link } from '@appsumo/dorado-react';
import { formatToHumanReadableDate } from '~/lib/util';
import PlusBadge from '~/public/plus-badge.png';
import VerifiedPuchasedBadge from '~/public/verified-purchased-badge.png';

export interface IDiscussionUserInfoProps {
  username: string;
  avatar: string;
  isVerified: boolean;
  isPlus: boolean;
  memberDate: Date;
  dealsBought: number;
  userNamePadding?: boolean;
}

function DiscussionUserInfo({
  username,
  avatar,
  isVerified,
  isPlus,
  memberDate,
  dealsBought,
  userNamePadding = false,
}: Readonly<IDiscussionUserInfoProps>) {
  return (
    <div className="w-full" data-testid="discussion-user-info">
      <Link
        href={`/profile/${username}/`}
        target="_blank"
        className="flex items-center gap-2"
      >
        <Image
          className="h-6 w-6 shrink-0 rounded-full object-cover"
          src={avatar}
          alt={username}
          height={24}
          width={24}
          loader={({ src }) =>
            avatar?.includes('gravatar') ? src : `${src}?w=24&h=24`
          }
        />
        <p
          className={clsx([
            'truncate font-bold text-black-pearl',
            userNamePadding && 'pr-5',
          ])}
        >
          {username}
        </p>
      </Link>
      {(isVerified || isPlus) && (
        <div
          className={`flex items-center gap-3 ${
            !isVerified ? 'mt-1.5' : 'mt-0.5'
          }`}
        >
          {isVerified && (
            <div className="flex items-center gap-1">
              <Image
                src={VerifiedPuchasedBadge}
                alt="Verified Purchaser badge"
                role="presentation"
                width={16}
                height={16}
              />
              <p className="text-sm font-bold leading-6 text-black-pearl">
                Verified purchaser
              </p>
            </div>
          )}
          {isPlus && (
            <Image
              className="h-auto w-[36px]"
              data-testid="plus-badge"
              src={PlusBadge}
              alt="Plus badge"
              role="presentation"
              width={30}
              height={15}
            />
          )}
        </div>
      )}
      <div
        className={`flex items-center gap-2 text-sm text-grace ${
          isVerified || isPlus ? 'mt-0.5' : 'mt-2'
        }`}
      >
        <span>
          Member since:{' '}
          {formatToHumanReadableDate(memberDate.toISOString(), {
            month: 'short',
          })}
        </span>
        {dealsBought > 0 && (
          <>
            <span
              className="h-1.5 w-1.5 rounded-full bg-gravel"
              aria-hidden="true"
            />
            <span>Deals bought: {dealsBought}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default DiscussionUserInfo;
